import React, { useEffect, useState } from 'react';
import {
  Box,
  Text,
  Heading,
  VStack,
  HStack,
  Badge,
  useColorModeValue,
  SimpleGrid,
  Flex,
  Icon,
  Select,
} from '@chakra-ui/react';
import { FaBox, FaTruck, FaUser, FaPhone, FaMapMarkerAlt } from 'react-icons/fa';
import { OrderDetailResponse, OrderStatus, OrderStatusLabel } from '../types/order.types';

const statusColors: Record<OrderStatus, string> = {
  [OrderStatus.PROCESSING]: 'blue',
  [OrderStatus.SHIPPED]: 'purple',
  [OrderStatus.DELIVERED]: 'green',
  [OrderStatus.CANCELLED]: 'red',
  [OrderStatus.RETURNED]: 'orange',
};

interface OrderDetailProps {
  orderData: OrderDetailResponse;
  isModal?: boolean;
  onStatusChange?: (orderId: number, newStatus: OrderStatus) => void;
  isAdmin?: boolean;
}

export const OrderDetail: React.FC<OrderDetailProps> = ({
  orderData,
  isModal = false,
  onStatusChange,
  isAdmin = false,
}) => {
  const [currentStatus, setCurrentStatus] = useState<OrderStatus>(Number(orderData.order.status) as OrderStatus);

  useEffect(() => {
    setCurrentStatus(Number(orderData.order.status) as OrderStatus);
  }, [orderData.order.status]);

  const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newStatus = Number(e.target.value) as OrderStatus;
    setCurrentStatus(newStatus);
    onStatusChange?.(orderData.order.id, newStatus);
  };

  const bgColor = useColorModeValue('gray.100', '#0B192C');
  const cardBgColor = useColorModeValue('white', '#1E3E62');
  const textColorPrimary = useColorModeValue('gray.800', 'whiteAlpha.900');
  const textColorSecondary = useColorModeValue('gray.600', 'gray.400');
  const borderColor = useColorModeValue('gray.200', '#4A5568');
  const priceColor = useColorModeValue('orange.300', '#FF6500');

  if (!orderData) {
    return (
      <Box p={8} bg={bgColor} minH="100vh">
        <Text>Sipariş bulunamadı.</Text>
      </Box>
    );
  }

  const { order, orderItems, shippingAddress } = orderData;

  return (
    <Box p={isModal ? 0 : [4, 6, 8]} bg={isModal ? 'transparent' : bgColor} minH={isModal ? 'auto' : '100vh'}>
      {!isModal && (
        <Heading size="xl" mb={8} color={textColorPrimary}>
          Sipariş Detayı
        </Heading>
      )}

      <SimpleGrid columns={[1, null, 2]} spacing={8}>
        {/* Sipariş Bilgileri */}
        <Box bg={cardBgColor} p={6} borderRadius="xl" boxShadow="lg">
          <Heading size="md" mb={4} color={textColorPrimary}>
            Sipariş Bilgileri
          </Heading>
          <VStack align="stretch" spacing={4}>
            <HStack>
              <Icon as={FaBox} color={textColorSecondary} />
              <Text color={textColorPrimary}>Sipariş No: {order.id}</Text>
            </HStack>
            <HStack>
              <Icon as={FaTruck} color={textColorSecondary} />
              <Text color={textColorPrimary}>
                Durum: <Badge colorScheme={statusColors[currentStatus]}>{OrderStatusLabel.get(currentStatus)}</Badge>
              </Text>
            </HStack>
            <HStack>
              <Icon as={FaUser} color={textColorSecondary} />
              <Text color={textColorPrimary}>Müşteri: {shippingAddress.fullName}</Text>
            </HStack>
            <HStack>
              <Icon as={FaPhone} color={textColorSecondary} />
              <Text color={textColorPrimary}>Telefon: {shippingAddress.phoneNumber}</Text>
            </HStack>
            <HStack>
              <Icon as={FaMapMarkerAlt} color={textColorSecondary} />
              <Text color={textColorPrimary}>Adres: {shippingAddress.address}</Text>
            </HStack>
          </VStack>
        </Box>

        {/* Sipariş Özeti */}
        <Box bg={cardBgColor} p={6} borderRadius="xl" boxShadow="lg">
          <Heading size="md" mb={4} color={textColorPrimary}>
            Sipariş Özeti
          </Heading>
          <VStack align="stretch" spacing={4}>
            <Flex justify="space-between">
              <Text color={textColorSecondary}>Toplam Ürün:</Text>
              <Text color={textColorPrimary} fontWeight="bold">
                {orderItems.length}
              </Text>
            </Flex>
            <Flex justify="space-between">
              <Text color={textColorSecondary}>Toplam Tutar:</Text>
              <Text color={priceColor} fontWeight="bold">
                {order.totalPrice.toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}
              </Text>
            </Flex>
            <Flex justify="space-between">
              <Text color={textColorSecondary}>Sipariş Tarihi:</Text>
              <Text color={textColorPrimary}>{new Date(order.createdAt).toLocaleDateString('tr-TR')}</Text>
            </Flex>
            <Flex justify="space-between">
              <Text color={textColorSecondary}>Son Güncelleme:</Text>
              <Text color={textColorPrimary}>
                {new Date(order.updatedAt).toLocaleDateString('tr-TR')}{' '}
                {new Date(order.updatedAt).toLocaleTimeString('tr-TR')}
              </Text>
            </Flex>
            {isAdmin && (
              <Box mt={2}>
                <Text color={textColorSecondary} mb={2}>
                  İşlemler
                </Text>
                <Select value={currentStatus} onChange={handleStatusChange} size="sm" width="200px">
                  {Array.from(OrderStatusLabel.entries()).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </Select>
              </Box>
            )}
          </VStack>
        </Box>
      </SimpleGrid>

      {/* Ürün Listesi */}
      <Box mt={8} bg={cardBgColor} p={6} borderRadius="xl" boxShadow="lg">
        <Heading size="md" mb={4} color={textColorPrimary}>
          Ürünler
        </Heading>
        <VStack spacing={4} align="stretch">
          {orderItems.map((item: any) => (
            <Box
              key={item.id}
              p={4}
              borderWidth="1px"
              borderRadius="md"
              borderColor={borderColor}
              bg={useColorModeValue('white', '#2D3748')}
            >
              <Flex>
                <Box flex="1">
                  <Text fontWeight="bold" color={textColorPrimary}>
                    {item.productName || 'Ürün Adı Bulunamadı'}
                  </Text>
                  {item.productVariant && (
                    <Text color={textColorSecondary} fontSize="sm">
                      {item.productVariant.variantName}: {item.productVariant.variantValue}
                    </Text>
                  )}
                  <Text color={textColorSecondary} fontSize="sm">
                    Adet: {item.quantity}
                  </Text>
                  <Text color={priceColor} fontWeight="bold">
                    {(item.unitPrice || 0).toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}
                  </Text>
                </Box>
                <Box textAlign="right">
                  <Text color={priceColor} fontWeight="bold">
                    {((item.unitPrice || 0) * item.quantity).toLocaleString('tr-TR', {
                      style: 'currency',
                      currency: 'TRY',
                    })}
                  </Text>
                </Box>
              </Flex>
            </Box>
          ))}
        </VStack>
      </Box>
    </Box>
  );
};
